import { microsoftStartUrl } from "./session";

/**
 * Resuming a lapsed Microsoft session, and the two bounds that keep it safe.
 *
 * Two `sessionStorage` keys, neither a credential: one spends this tab's single resume
 * attempt so a failing resume cannot loop between here and Entra, and one carries the
 * route across the round trip, because `returnTo` can only name an origin. A module
 * variable holds the third fact - whether this document has already handed the window
 * over - precisely because that one must *not* survive the navigation.
 */
/**
 * One attempt per tab, so a resume that keeps failing degrades to the login form
 * instead of bouncing the browser between this app and Entra.
 *
 * The bounded case is the ordinary one: the marker says Microsoft, `/start` is
 * taken, Entra re-admits, and the session is seated. The unbounded one is real
 * though — anything that leaves us anonymous *after* a successful handshake
 * (clock skew against the token's `exp`, a cookie the browser declines to store,
 * a session lost between the callback and the next request) puts us straight back
 * at the same decision with the same marker, and nothing in the round trip
 * changes it. That is a redirect loop with no exit and no error to read.
 *
 * `sessionStorage` rather than a module variable because the loop crosses a
 * document load: a module variable is reset by the very navigation whose repeat
 * needs detecting. Per-tab and cleared by the browser, which is the right
 * lifetime — a new tab is a new attempt.
 */
const RESUME_ATTEMPTED_KEY = "om.auth.microsoftResumeAttempted";

/**
 * Whether this tab has already spent its attempt.
 *
 * Throwing storage answers `true` — "treat it as spent" — rather than `false`, because
 * with no flag surviving the navigation *every* new document would attempt again and a
 * resume that keeps failing would never terminate.
 *
 * It is not what enforces that, though, and the comment used to imply otherwise:
 * `markAttempted` reads its own write back, so any store whose `getItem` throws is
 * refused there anyway. This branch therefore changes no outcome by itself, and no test
 * can isolate it. It stays as the locally safe answer — the two functions are read
 * separately, and this one should not depend on the other getting it right — not as a
 * load-bearing guard.
 */
function attemptSpent(): boolean {
  try {
    return sessionStorage.getItem(RESUME_ATTEMPTED_KEY) === "1";
  } catch {
    return true;
  }
}

/**
 * Record the attempt, reporting whether it will actually survive the navigation.
 *
 * Read back rather than trusted: a quota-limited or partitioned store can accept a
 * write and not keep it, and an unpersisted flag bounds nothing.
 */
function markAttempted(): boolean {
  try {
    sessionStorage.setItem(RESUME_ATTEMPTED_KEY, "1");
    return sessionStorage.getItem(RESUME_ATTEMPTED_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Whether this document has already handed the window over.
 *
 * A module variable precisely because the flag above could not be one: that flag
 * has to survive the navigation, and this has to *not* survive it. Its question is
 * "am I already on my way out of this document", which a fresh document answers
 * `false` by existing.
 *
 * Needed because a navigation is not instant. Any re-render between `assign` and
 * the document being torn down asks the decision again, and without this the answer
 * flips to `false` — the flag is spent by then — so the caller renders the login
 * form it falls back to. React's development double render asks immediately and
 * reliably, but it is not the only thing that can: a state update landing in that
 * window does it in production too, and the user sees a login form flash over a
 * sign-in that is already succeeding.
 */
let handedOver = false;

/**
 * Where the user was when the resume took the window.
 *
 * `returnTo` cannot carry it: the API compares that value against its configured
 * origins as an exact string, so it is an origin and nothing more, and the callback
 * lands on `/`. Without this a Microsoft user reading
 * `/batches/<id>?tab=issues` is returned to the dashboard at every expiry, losing
 * the route and any URL-backed filters with it - roughly hourly, since the session
 * is exactly as long as the ID token.
 *
 * Same store and same lifetime as the attempt flag above, for the same reason: the
 * round trip is a document load, so nothing in memory survives it.
 */
const RESUME_TARGET_KEY = "om.auth.microsoftResumeTarget";

/**
 * One definition, because this is the open-redirect boundary and it is checked twice -
 * on the way in and again on the way out. `sessionStorage` is writable by anything
 * running on this origin, so an absolute URL reaching the restore would be a redirect
 * to wherever it says.
 */
function isSameDocumentPath(value: string | null | undefined): value is string {
  // A backslash is rejected in the authority position as well as a slash: the URL
  // parser treats the two identically there, so `/\evil.example/x` resolves to
  // `https://evil.example/x` and slips straight through a `startsWith("//")` check.
  return !!value && /^\/(?![/\\])/.test(value);
}

/**
 * The stashed route, if there is one. Left in place.
 *
 * Read without consuming, because the restore competes with whatever else navigates
 * on arrival - a router index redirect mounts in the same commit, and `StrictMode`
 * replays its effect. Consuming on the first read let that replay overwrite the
 * route this was pointing at, with nothing left to try again from. The caller clears
 * it once the router has actually arrived.
 */
export function readMicrosoftResumeTarget(): string | null {
  try {
    const target = sessionStorage.getItem(RESUME_TARGET_KEY);
    return isSameDocumentPath(target) ? target : null;
  } catch {
    return null;
  }
}

/** Forget it - arrived, or given up on. */
export function clearMicrosoftResumeTarget(): void {
  try {
    sessionStorage.removeItem(RESUME_TARGET_KEY);
  } catch {
    // Nothing to clear if it could never be written.
  }
}

/** Called once a session is actually established, so the next lapse may resume. */
export function clearMicrosoftResumeAttempt(): void {
  handedOver = false;
  try {
    sessionStorage.removeItem(RESUME_ATTEMPTED_KEY);
  } catch {
    // Nothing to clear if it could never be written.
  }
}

/**
 * Hand the window to `/start` if a Microsoft session plausibly lapsed here and we
 * have not already tried this tab. Returns whether it navigated, so the caller
 * can render a loader rather than the login form it was about to show.
 */
export function resumeMicrosoftSession(): boolean {
  // Still `true`: the window is already going to `/start`, and answering otherwise
  // would have the caller render its fallback over a navigation in flight.
  if (handedOver) return true;
  if (attemptSpent()) return false;
  // Only navigate once the flag is provably persisted: an unbounded resume is a
  // worse failure than a login form.
  if (!markAttempted()) return false;
  try {
    // Best-effort: losing the route costs the user a navigation, where refusing to
    // resume over it would cost them a password.
    const here = window.location.pathname + window.location.search;
    if (isSameDocumentPath(here)) sessionStorage.setItem(RESUME_TARGET_KEY, here);
  } catch {
    // Same store that just proved writable for the flag, but not worth coupling.
  }
  handedOver = true;
  window.location.assign(microsoftStartUrl());
  return true;
}
